import { Component, input, output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DocumentService } from '../../services/document.service';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule]
})
export class DocumentUploadComponent {
  uuid = input.required<string>();
  origin = input.required<'PARTY' | 'VEHICLE' | 'CLAIM'>();
  onSuccess = output<void>();

  private docService = inject(DocumentService);
  private optimizer = inject(ImageOptimizerService);
  isBusy = signal(false);

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true
  });

  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || this.isBusy()) return;
    void this._processAndUpload(file);
  }

  private async _processAndUpload(file: File): Promise<void> {
    this.isBusy.set(true);
    try {
      const finalFile = await this._optimizeIfNeeded(file);
      await this._uploadToServer(finalFile);
    } catch {
      this.Toast.fire({ icon: 'error', title: 'Error al subir el documento' });
    } finally {
      this.isBusy.set(false);
    }
  }

  private async _optimizeIfNeeded(file: File): Promise<File> {
    if (!file.type.startsWith('image/')) return file;
    try {
      const optimized = await this.optimizer.optimize(file);
      return optimized || file;
    } catch {
      return file;
    }
  }

  private _uploadToServer(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      this.docService.uploadDocument(this.uuid(), file, this.origin()).subscribe({
        next: () => {
          this.Toast.fire({ icon: 'success', title: 'Documento subido correctamente' });
          this.onSuccess.emit();
          resolve();
        },
        error: () => {
          this.Toast.fire({ icon: 'error', title: 'Error al subir el documento' });
          reject();
        }
      });
    });
  }

  triggerClick(fileInput: HTMLInputElement): void {
    if (this.isBusy()) return;
    fileInput.click();
  }
}
